'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import { useInject } from '@actr/di/src/hook/use-inject';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import seoReviewsDIModule from '@actr/stores/src/modules/seo/reviews';
import { AviaSearchFormStore } from '@actr/stores/src/stores/stores/avia/search-form';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Container } from '@actr/ui/src/components/container';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, useEffect } from 'react';

import AirlineCities from '@/components/avia/airline/airline-cities';
import PopularFlights from '@/components/avia/popular-flights';
import SearchFormHeader from '@/components/avia/search-form-header';
import Breadcrumb from '@/components/common/breadcrumb';
import ServiceAdvantages from '@/components/page-constucts/advantages';
import Faq from '@/components/page-constucts/faq';
import InnerHtmlTextBlock from '@/components/page-constucts/inner-html-text-block';
import ReviewsBlock from '@/components/page-constucts/reviews-block';
import SideNavBar from '@/components/page-constucts/side-navigation-bar';
import { SeoAirline } from '@/services/models/avia/airline';
import { getContentBlocks } from '@/utils/get-content-blocks';
import getNavBarItems from '@/utils/nav-bar';

import * as Styled from './style.css';

export type BaseAirlinePageProps = {
  data: SeoAirline;
  path: string;
};

const BaseAirlinePage: FC<BaseAirlinePageProps & PropsWithChildren> = observer(
  ({ data, children, path }) => {
    const { search_form_title, breadcrumbs, content } = data;
    const {
      info,
      popular_airlines_flights,
      cities_for_airline,
      faq,
      feedback,
      advantages,
      app_banner,
    } = content || {};
    const aviaSearchForm = useInject(AviaSearchFormStore);
    const hasPopularAirlinesFlights = !!popular_airlines_flights && !!popular_airlines_flights.list;

    const itemsForNavBar = getNavBarItems(content);

    useEffect(() => {
      const defaultSegments = [{ ...aviaSearchForm.defaultSegment }];

      aviaSearchForm.stopStore();
      runInAction(() => {
        aviaSearchForm.setSegments(defaultSegments);
        aviaSearchForm.setDirection(aviaSearchFormDirections.TWOWAY);
      });
    }, [aviaSearchForm]);

    const crumbFrom = [
      {
        title: 'Дешёвые авиабилеты',
        path: '/',
      },
      {
        path: '/airlines',
        title: 'Авиакомпании',
      },
      {
        path: `/airlines/${path}`,
        title: breadcrumbs,
      },
    ];

    const contentBlocks = content
      ? [
        {
          key: 'info',
          condition: !!info?.description,
          render: () => info?.description ? (
            <ContentBlock id="airline_information">
              <InnerHtmlTextBlock
                html={info.description}
              />
            </ContentBlock>
          ) : null,
        },
        {
          key: 'cities_for_airline',
          condition: !!cities_for_airline && !!cities_for_airline.list,
          render: () => cities_for_airline ? (
            <AirlineCities
              title={cities_for_airline.title}
              cities={cities_for_airline.list}
            />
          ) : null
        },
        {
          key: 'popular_airlines_flights',
          condition: hasPopularAirlinesFlights,
          render: () => (
            popular_airlines_flights ? (
              <PopularFlights
                navigationId="popular_airlines_flights"
                title={popular_airlines_flights.title}
                flights={popular_airlines_flights.list}
              />
            ) : null
          )
        },
        {
          key: 'advantages',
          condition: !!advantages?.list?.enable,
          render: () => <ServiceAdvantages />,
        },
        {
          key: 'faq',
          condition: !!faq?.list,
          render: () => faq ? <Faq list={faq?.list} title={faq.title} /> : null,
        },
        {
          key: 'feedback',
          condition: !!feedback?.list,
          render: () => feedback ? (
            <ReviewsBlock
              title={feedback.title}
              feedback={feedback?.list}
            />
          ) : null,
        },
      ] : [];

    const blocks = getContentBlocks({
      content,
      contentBlocks
    });

    return (
      <>
        <SearchFormHeader title={search_form_title || ''} />
        <Container>
          <Breadcrumb crumb={crumbFrom} dynamic={false} />
          <Space size={Sizes.M} />
          <Container className={Styled.ColumnWrapper}>
            <SideNavBar items={itemsForNavBar} isAppBannerEnable={!!app_banner?.list?.enable} isHideNavBarOnMobile={true} />
            <div className={Styled.MainInfo}>
              {blocks.map(block => (
                <React.Fragment key={block.key}>
                  {block.render()}
                </React.Fragment>
              ))}
            </div>
          </Container>
          {children}
        </Container>
      </>
    );
  }
);

export default loadDIModules(
  BaseAirlinePage,
  aviaSearchDIModule,
  seoReviewsDIModule
);
